import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import api, { formatErr } from "@/lib/api";
import { Loader2, Mail, Lock, CheckCircle2, ArrowRight, ShieldCheck, KeyRound } from "lucide-react";

export default function Login() {
  const { login, completeLogin } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [remember, setRemember] = useState(false);
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");

  // 2FA step
  const [step, setStep] = useState("creds");
  const [tfa, setTfa] = useState(null);
  const [code, setCode] = useState("");
  const [useRecovery, setUseRecovery] = useState(false);
  const [recovery, setRecovery] = useState("");
  const [cooldown, setCooldown] = useState(0);

  const goNext = () => navigate(params.get("next") || "/", { replace: true });

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (mode === "login") {
        const data = await login(email.trim(), password, remember);
        if (data?.twofa_required) {
          setTfa(data);
          setStep("2fa");
          toast.success(`Verification code sent to ${data.destination}`);
        } else {
          goNext();
        }
      } else {
        await api.post("/auth/forgot-password", { email: email.trim() });
        toast.success("If the account exists, a reset link has been sent.");
        setMode("login");
      }
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Unable to sign in");
    } finally {
      setLoading(false);
    }
  };

  const verify2fa = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const body = {
        challenge_token: tfa.challenge_token,
        remember,
        trust_device: remember,
        device_label: navigator.userAgent.slice(0, 90),
      };
      if (useRecovery) body.recovery_code = recovery.trim();
      else body.code = code.trim();
      const { data } = await api.post("/auth/2fa/verify", body);
      await completeLogin(data.token, data.device_token);
      goNext();
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail) || "Verification failed");
    } finally {
      setLoading(false);
    }
  };

  const resend = async () => {
    if (cooldown > 0) return;
    try {
      await api.post("/auth/2fa/resend", { challenge_token: tfa.challenge_token });
      toast.success("A new code has been sent.");
      let t = 30;
      setCooldown(t);
      const iv = setInterval(() => {
        t -= 1;
        setCooldown(t);
        if (t <= 0) clearInterval(iv);
      }, 1000);
    } catch (err) {
      toast.error(formatErr(err.response?.data?.detail));
    }
  };

  const backToCreds = () => {
    setStep("creds"); setTfa(null); setCode(""); setRecovery(""); setUseRecovery(false); setPassword("");
  };

  return (
    <div className="min-h-screen flex">
      {/* Brand panel */}
      <div className="hidden lg:flex w-1/2 bg-[#0F1724] relative overflow-hidden flex-col justify-between p-12">
        <div className="absolute inset-0 cc-grid-bg opacity-40" />
        <div className="absolute -top-24 -right-24 h-80 w-80 rounded-full bg-[#00C9E0]/20 blur-3xl" />
        <div className="absolute bottom-10 -left-20 h-72 w-72 rounded-full bg-[#FE3967]/10 blur-3xl" />
        <div className="relative bg-white rounded-xl px-5 py-4 w-fit shadow-lg"><Logo className="h-10" /></div>
        <div className="relative text-white max-w-md">
          <h1 className="font-display text-4xl font-extrabold leading-tight">The sales CRM built for local advertising teams.</h1>
          <p className="mt-4 text-slate-300">Track every lead, pipeline, advertiser and commission in one branded workspace — from first call to signed contract.</p>
          <ul className="mt-8 space-y-3">
            {["Multi-tenant & white-label ready", "Pipeline, contracts & commission engine", "Territory, zone & publication tracking"].map((t) => (
              <li key={t} className="flex items-center gap-3 text-sm text-slate-200">
                <CheckCircle2 className="h-5 w-5 text-[#9ACC32]" /> {t}
              </li>
            ))}
          </ul>
        </div>
        <div className="relative text-xs text-slate-400">ChitChat Sales CRM · crm.chitchatlocal.ca</div>
      </div>

      {/* Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-[#F8FAFC]">
        <div className="w-full max-w-md">
          <div className="lg:hidden mb-8 flex justify-center"><Logo /></div>
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-8">
            {step === "2fa" ? (
              <>
                <div className="flex items-center gap-2 text-[#00C9E0]"><ShieldCheck className="h-6 w-6" /></div>
                <h2 className="font-display text-2xl font-bold text-[#1E2025] mt-2">Two-step verification</h2>
                <p className="text-sm text-slate-500 mt-1">
                  {useRecovery
                    ? "Enter one of your saved recovery codes."
                    : `We sent a 6-digit code to ${tfa?.destination}. It expires in 10 minutes.`}
                </p>
                <form onSubmit={verify2fa} className="mt-6 space-y-4" data-testid="twofa-form">
                  {useRecovery ? (
                    <div>
                      <Label className="text-xs font-medium text-slate-600">Recovery code</Label>
                      <div className="relative mt-1">
                        <KeyRound className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input value={recovery} onChange={(e) => setRecovery(e.target.value)} required
                          className="pl-9 tracking-widest" placeholder="XXXXX-XXXXX" data-testid="twofa-recovery-input" />
                      </div>
                    </div>
                  ) : (
                    <div>
                      <Label className="text-xs font-medium text-slate-600">Verification code</Label>
                      <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                        required inputMode="numeric" autoFocus
                        className="mt-1 text-center text-2xl tracking-[0.5em] font-semibold" placeholder="••••••"
                        data-testid="twofa-code-input" />
                    </div>
                  )}
                  <Button type="submit" className="w-full rounded-lg h-11" disabled={loading} data-testid="twofa-submit">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Verify & sign in <ArrowRight className="h-4 w-4" /></>)}
                  </Button>
                  <div className="flex items-center justify-between text-sm">
                    {!useRecovery ? (
                      <button type="button" onClick={resend} disabled={cooldown > 0}
                        className="text-[#0094A8] hover:underline disabled:text-slate-400 disabled:no-underline" data-testid="twofa-resend">
                        {cooldown > 0 ? `Resend in ${cooldown}s` : "Resend code"}
                      </button>
                    ) : <span />}
                    {tfa?.can_use_recovery && (
                      <button type="button" onClick={() => setUseRecovery((v) => !v)} className="text-slate-500 hover:underline" data-testid="twofa-toggle-recovery">
                        {useRecovery ? "Use the emailed code" : "Use a recovery code"}
                      </button>
                    )}
                  </div>
                  <button type="button" onClick={backToCreds} className="w-full text-sm text-slate-500 hover:underline" data-testid="twofa-back">
                    Back to sign in
                  </button>
                </form>
              </>
            ) : (
              <>
                <h2 className="font-display text-2xl font-bold text-[#1E2025]">{mode === "login" ? "Welcome back" : "Reset password"}</h2>
                <p className="text-sm text-slate-500 mt-1">{mode === "login" ? "Sign in to your ChitChat CRM account." : "We'll email you a reset link."}</p>
                <form onSubmit={submit} className="mt-6 space-y-4" data-testid="login-form">
                  <div>
                    <Label className="text-xs font-medium text-slate-600">Email</Label>
                    <div className="relative mt-1">
                      <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                      <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} className="pl-9" placeholder="you@company.com" data-testid="login-email" />
                    </div>
                  </div>
                  {mode === "login" && (
                    <div>
                      <Label className="text-xs font-medium text-slate-600">Password</Label>
                      <div className="relative mt-1">
                        <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                        <Input type="password" required value={password} onChange={(e) => setPassword(e.target.value)} className="pl-9" placeholder="••••••••" data-testid="login-password" />
                      </div>
                    </div>
                  )}
                  {mode === "login" && (
                    <div className="flex items-center justify-between">
                      <label className="flex items-center gap-2 text-sm text-slate-600 cursor-pointer">
                        <Checkbox checked={remember} onCheckedChange={setRemember} data-testid="remember-device" /> Remember this device
                      </label>
                      <button type="button" onClick={() => setMode("forgot")} className="text-sm text-[#0094A8] hover:underline" data-testid="forgot-link">Forgot password?</button>
                    </div>
                  )}
                  <Button type="submit" className="w-full rounded-lg h-11" disabled={loading} data-testid="login-submit">
                    {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>{mode === "login" ? "Sign In" : "Send reset link"} <ArrowRight className="h-4 w-4" /></>)}
                  </Button>
                  {mode === "forgot" && <button type="button" onClick={() => setMode("login")} className="w-full text-sm text-slate-500 hover:underline">Back to sign in</button>}
                </form>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
